// src/features/explore/SearchResults.tsx — the server-driven results view (1.24.00 B3).
//
// Rendered by ExplorePage whenever the box holds >= 2 characters, replacing the
// client-side browsing (chips / discovery / filterExplore) for that mode. Three
// tabs with live counts — Posts (PostCard rows), People (PersonCard rows),
// Hashtags (chips that re-query as "#tag") — plus honest empty / error /
// searching states. Purely presentational over useSearch's output; the hook owns
// the debounce and the in-flight sequence guard.

import { useState } from 'react';
import styled from 'styled-components';
import { Tabs } from '@/ui';
import { Icon } from '@/icons';
import { PostCard } from '@/components/PostCard';
import { formatCount } from '@/components/formatCount';
import type { SearchResults as SearchResultsPayload } from '@/core/models/search';
import { PersonCard } from './PersonCard';

type ResultTab = 'posts' | 'people' | 'hashtags';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PeopleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
`;

const ChipRow = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HashChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  &:hover { filter: brightness(1.12); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.focusRing}; outline-offset: 2px; }
`;

const ChipCount = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 400;
  color: ${({ theme }) => theme.color.textMuted};
`;

const State = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Searching = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface SearchResultsProps {
  query: string;
  loading: boolean;
  error: string | null;
  results: SearchResultsPayload | null;
  /** A hashtag chip was picked — the page sets its box to "#tag" and re-queries. */
  onPickHashtag: (tag: string) => void;
}

export function SearchResults({ query, loading, error, results, onPickHashtag }: SearchResultsProps) {
  // null = the member has not picked a tab; land on the first non-empty list.
  const [picked, setPicked] = useState<ResultTab | null>(null);
  const posts = results?.posts ?? [];
  const people = results?.people ?? [];
  const hashtags = results?.hashtags ?? [];
  const nothing = !!results && posts.length === 0 && people.length === 0 && hashtags.length === 0;
  const tab: ResultTab =
    picked ??
    (posts.length > 0 ? 'posts' : people.length > 0 ? 'people' : hashtags.length > 0 ? 'hashtags' : 'posts');

  const items = [
    { value: 'posts', label: `Posts (${formatCount(posts.length)})` },
    { value: 'people', label: `People (${formatCount(people.length)})` },
    { value: 'hashtags', label: `Hashtags (${formatCount(hashtags.length)})` },
  ];

  return (
    <Wrap aria-label="Search results">
      <Tabs items={items} value={tab} onChange={(v) => setPicked(v as ResultTab)} />

      {loading && <Searching role="status" aria-live="polite">Searching…</Searching>}

      {error && !loading && <State role="alert">{error}</State>}

      {!error && nothing && !loading && (
        <State role="status">Nothing matched “{query.trim()}” yet.</State>
      )}

      {!error && results && !nothing && (
        <>
          {tab === 'posts' &&
            (posts.length === 0 ? (
              <State role="status">No posts matched “{query.trim()}”.</State>
            ) : (
              <Stack>
                {posts.map((p, i) => (
                  <PostCard key={p.id} post={p} index={i} />
                ))}
              </Stack>
            ))}

          {tab === 'people' &&
            (people.length === 0 ? (
              <State role="status">No people matched “{query.trim()}”.</State>
            ) : (
              <PeopleGrid>
                {people.map((p) => (
                  <PersonCard key={p.handle} profile={p} />
                ))}
              </PeopleGrid>
            ))}

          {tab === 'hashtags' &&
            (hashtags.length === 0 ? (
              <State role="status">No hashtags matched “{query.trim()}”.</State>
            ) : (
              <ChipRow aria-label="Matching hashtags">
                {hashtags.map((h) => (
                  <li key={h.tag}>
                    <HashChip
                      type="button"
                      onClick={() => onPickHashtag(h.tag)}
                      aria-label={`#${h.tag}, ${h.count === 1 ? '1 post' : `${formatCount(h.count)} posts`}`}
                    >
                      <Icon name="hash" size={14} />
                      {h.tag}
                      <ChipCount>{formatCount(h.count)}</ChipCount>
                    </HashChip>
                  </li>
                ))}
              </ChipRow>
            ))}
        </>
      )}
    </Wrap>
  );
}
